import {
  CompanyAnalysis,
  CompanyAnalysisWarning,
  CompanyChart,
  CompanyContact,
  CompanyContactStatus,
  CompanyDataAsset,
  CreditBoxReport,
  ReportType,
  PrincipalInvestment,
  Company,
  CompanyIdParam,
  ChartsByTabNameParams,
  CompanyAnalysisParams,
  Pagination,
  UserIdentity,
} from '@hum/types';
import { createQuery } from '../../utils';
import { toSnakeCase } from '../../utils/createApiClient/snakeCased';

export const getCreditBoxReports = createQuery<
  CreditBoxReport[],
  CompanyIdParam
>(
  async ({ api, params }) => {
    const reportTypes = Object.values(ReportType);

    const reportPromises = reportTypes.map(async (reportType) => {
      const response = await api.get(
        `/v2/companies/${params.companyId}/reports?report_type=${reportType}`
      );
      return response?.items.map(
        (item) =>
          ({
            id: item.id,
            companyId: item.companyId,
            createdAt: item.createdAt,
            type: item.reportType,
            status: item.status,
            rating: item.investorTestRating,
          } as CreditBoxReport)
      );
    });

    const reports = await Promise.all(reportPromises);
    return reports.flat();
  },
  {
    scope: 'getCreditBoxReports',
    errorToast: { message: 'Failed to fetch credit box reports' },
  }
);

export const getCreditBoxReport = createQuery<
  CreditBoxReport,
  {
    companyId: string;
    reportId: string;
  }
>(({ api, params }) => {
  return api.get(
    `/v2/companies/${params.companyId}/reports/${params.reportId}`
  );
});

export const getCompanyUsers = createQuery<
  UserIdentity[],
  { companyId: string }
>(
  ({ api, params, router }) => {
    const companyId = router.query.company_id || params.companyId;
    return api.get(`/organizations/${companyId}/users`).then((response) => {
      const priority = {
        [CompanyContactStatus.Active]: 0,
        [CompanyContactStatus.Invited]: 1,
        [CompanyContactStatus.Disabled]: 2,
      };

      return response.sort((a, b) => priority[a.status] - priority[b.status]);
    });
  },
  {
    apiPrefix: 'identity',
  }
);

export const getCompanyContact = createQuery<CompanyContact, CompanyIdParam>(
  ({ api, params }) => {
    return api.get(`/v2/companies/${params.companyId}/contact`);
  }
);

export const getChartsByTabName = createQuery<
  CompanyChart[],
  ChartsByTabNameParams
>(({ api, params }) => {
  return api.get(`/v2/companies/${params.companyId}/charts/${params.tabName}`);
});

export const getCompanyAnalyses = createQuery<
  CompanyAnalysis[],
  CompanyIdParam
>(({ api, params }) => {
  return api.get(`/v2/companies/${params.companyId}/company_analysis`);
});

export const getCompanyAnalysisWarnings = createQuery<
  CompanyAnalysisWarning[],
  CompanyAnalysisParams
>(({ api, params }) => {
  return api.get(
    `/v2/companies/${params.companyId}/company_analysis/${params.companyAnalysisId}/company_analysis_warnings`
  );
});

export const getCompanyFinancialSummary = createQuery<
  { status: boolean },
  CompanyIdParam
>(({ api, params }) => {
  return api.get(
    `/v2/companies/${params.companyId}/analytics_permissions?status=financial_summary`
  );
});

export const getInsightsCharts = createQuery<
  CompanyChart[],
  CompanyAnalysisParams
>(({ api, params }) => {
  return api
    .get(`/v2/companies/${params.companyId}/insights/charts`)
    .then((response) => {
      return toSnakeCase(response);
    });
});

export const getCompanyDataAssets = createQuery<
  CompanyDataAsset[],
  CompanyIdParam
>(({ api, params }) => {
  return api.get(`/v2/companies/${params.companyId}/data_assets`);
});

export const getPrincipalInvestments = createQuery<
  PrincipalInvestment[],
  CompanyIdParam
>(({ api, params }) => {
  return api
    .get(`/v2/companies/${params.companyId}/principal-investments`)
    .then((response) => {
      const { balanceSheetPartners, ...rest } = response;
      return {
        ...rest,
        interestedBalanceSheetPartners: balanceSheetPartners,
      };
    });
});

export const searchCompanies = createQuery<
  {
    companies: Company[];
    pagination: Pagination;
  },
  { filterQuery: string }
>(
  ({ api, params }) => {
    return api
      .get(`/companies/search?${params.filterQuery}`)
      .then((response) => ({
        companies: response.results,
        pagination: {
          pageCount: response.pagination.numPages,
          currentPage: response.pagination.pageNumber,
          pageSize: response.pagination.pageSize,
          results: response.pagination.totalResults,
        },
      }));
  },
  {
    errorToast: { message: 'Failed to get companies' },
  }
);
